import { Server } from 'miragejs';

import {
  frameThatFailsToActivate,
  frameThatFailsToActivateNoDraft,
  frameThatFailsToArchive,
  frameThatFailsToArchiveNoDraft,
  frameThatFailsToDeactivate,
  frameThatFailsToDeactivate2,
  frameThatFailsToDeactivateNoDraft,
  frameThatFailsToDeactivateNoDraft2,
  frameThatFailsToUnarchive,
  frameThatFailsToUnarchiveNoDraft,
} from 'mocks/server/data/frames';
import {
  linkedAssetThatFailsToActivate,
  linkedAssetThatFailsToActivateNoDraft,
  linkedAssetThatFailsToArchive,
  linkedAssetThatFailsToArchiveNoDraft,
  linkedAssetThatFailsToDeactivate,
  linkedAssetThatFailsToDeactivateNoDraft,
  linkedAssetThatFailsToUnarchive,
  linkedAssetThatFailsToUnarchiveNoDraft,
} from 'mocks/server/data/linkedAssets';
import LocalStorageManager from 'mocks/server/localStorageManager';
import { searchFrames, searchLinkedAssets } from 'mocks/server/utils/entitiesBySearch';
import { setStatusForEntities } from 'mocks/server/utils/entityUtils';
import { paged } from 'mocks/server/utils/paged';
import { ILiveFrame, ILiveLinkedAsset } from 'types/detailsEntityType';
import {
  ENTITY_ACTIVATE_API_URL,
  ENTITY_API_URL,
  ENTITY_ARCHIVE_API_URL,
  ENTITY_DEACTIVATE_API_URL,
  ENTITY_SEARCH_API_URL,
  ENTITY_UNARCHIVE_API_URL,
} from 'utils/defaults';
import { LiveStatus } from 'utils/statuses';

export const entitiesRoutes = (
  server: Server,
  frameStore: LocalStorageManager<ILiveFrame>,
  linkedAssetStore: LocalStorageManager<ILiveLinkedAsset>
) => {
  server.get(ENTITY_API_URL, (schema, request) => {
    const page = Number(request.queryParams.page);
    const pageSize = Number(request.queryParams.pageSize);

    const localFrames = frameStore.getAll();
    const localLinkedAssets = linkedAssetStore.getAll();

    const allEntities = [...localFrames, ...localLinkedAssets];

    const tableEntities = allEntities.map((liveEntity) => {
      return {
        spaceId: liveEntity.spaceId,
        name: liveEntity.name,
        environment: liveEntity.environment,
        formatSize: liveEntity.formatSize ? liveEntity.formatSize.name : null,
        entityType: liveEntity.entityType,
        mediaOwnerName: liveEntity.mediaOwnerName,
        status: liveEntity.status,
      };
    });

    return paged(tableEntities, page, pageSize);
  });

  server.get(ENTITY_SEARCH_API_URL, (schema, request) => {
    const searchTerm = request.queryParams.searchTerm as string;
    const page = Number(request.queryParams.page);
    const pageSize = Number(request.queryParams.pageSize);

    const localFrames = frameStore.getAll();
    const localLinkedAssets = linkedAssetStore.getAll();

    const searchedFrames = searchFrames(searchTerm, localFrames);
    const searchedLinkedAssets = searchLinkedAssets(searchTerm, localLinkedAssets);

    const searchResults = searchedFrames.concat(searchedLinkedAssets);

    const searchTableEntities = searchResults.map((liveEntity) => {
      return {
        spaceId: liveEntity.spaceId,
        name: liveEntity.name,
        environment: liveEntity.environment,
        formatSize: liveEntity.formatSize ? liveEntity.formatSize.name : null,
        entityType: liveEntity.entityType,
        mediaOwnerName: liveEntity.mediaOwnerName,
        status: liveEntity.status,
      };
    });

    return paged(searchTableEntities, page, pageSize);
  });

  const handleEntityAction = (
    requestBody: string,
    failedFrameIds: string[],
    failedFrameIdsNoDraft: string[],
    failedLinkedAssetIds: string[],
    failedLinkedAssetIdsNoDraft: string[],
    actionName: string,
    status: LiveStatus
  ) => {
    const spaceIds = JSON.parse(requestBody) as string[];

    const frameIds = spaceIds.filter((id) => frameStore.getById(id));
    const linkedAssetIds = spaceIds.filter((id) => linkedAssetStore.getById(id));

    setStatusForEntities(frameIds, frameStore, status);
    setStatusForEntities(linkedAssetIds, linkedAssetStore, status);

    const spaceIdsWithDraft: string[] = [];
    const spaceIdsWithoutDraft: string[] = [];

    spaceIds.forEach((id: string) => {
      if (failedFrameIds.includes(id) || failedLinkedAssetIds.includes(id)) {
        spaceIdsWithDraft.push(id);
      }

      if (failedFrameIdsNoDraft.includes(id) || failedLinkedAssetIdsNoDraft.includes(id)) {
        spaceIdsWithoutDraft.push(id);
      }
    });

    return {
      totalItemsActioned: spaceIds.length,
      spaceIdsWithDraft,
      spaceIdsWithoutDraft,
    };
  };

  server.patch(ENTITY_DEACTIVATE_API_URL, (schema, request) => {
    return handleEntityAction(
      request.requestBody,
      [frameThatFailsToDeactivate.spaceId, frameThatFailsToDeactivate2.spaceId],
      [frameThatFailsToDeactivateNoDraft.spaceId, frameThatFailsToDeactivateNoDraft2.spaceId],
      [linkedAssetThatFailsToDeactivate.spaceId],
      [linkedAssetThatFailsToDeactivateNoDraft.spaceId],
      'deactivation',
      LiveStatus.INACTIVE
    );
  });

  server.patch(ENTITY_ACTIVATE_API_URL, (schema, request) => {
    return handleEntityAction(
      request.requestBody,
      [frameThatFailsToActivate.spaceId],
      [frameThatFailsToActivateNoDraft.spaceId],
      [linkedAssetThatFailsToActivate.spaceId],
      [linkedAssetThatFailsToActivateNoDraft.spaceId],
      'activation',
      LiveStatus.ACTIVE
    );
  });

  server.patch(ENTITY_ARCHIVE_API_URL, (schema, request) => {
    return handleEntityAction(
      request.requestBody,
      [frameThatFailsToArchive.spaceId],
      [frameThatFailsToArchiveNoDraft.spaceId],
      [linkedAssetThatFailsToArchive.spaceId],
      [linkedAssetThatFailsToArchiveNoDraft.spaceId],
      'archiving',
      LiveStatus.ARCHIVED
    );
  });

  server.patch(ENTITY_UNARCHIVE_API_URL, (schema, request) => {
    return handleEntityAction(
      request.requestBody,
      [frameThatFailsToUnarchive.spaceId],
      [frameThatFailsToUnarchiveNoDraft.spaceId],
      [linkedAssetThatFailsToUnarchive.spaceId],
      [linkedAssetThatFailsToUnarchiveNoDraft.spaceId],
      'unarchiving',
      LiveStatus.INACTIVE
    );
  });
};
